import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import Navbar from '../../components/feature/Navbar';

// Hook para animación slide-up al hacer scroll
function useSlideUpOnScroll() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
          observer.unobserve(el);
        }
      },
      { threshold: 0.12 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return ref;
}

// Componente wrapper con animación slide-up
function SlideUpCard({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Estado inicial
    el.style.opacity = '0';
    el.style.transform = 'translateY(20px)';
    el.style.transition = `opacity 0.5s ease-out ${delay}ms, transform 0.5s ease-out ${delay}ms`;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}

export default function ServicesPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [heroLoaded, setHeroLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setHeroLoaded(true), 80);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Set document title and meta tags using React 19 approach
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    document.title = 'Services | Miami Comfort Group - Real Estate Consulting Solutions';
    
    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Comprehensive real estate consulting services including strategic consulting, company structuring, financing, market analysis, property acquisition, and property management in Miami, Florida.');
    
    // Update or create keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', 'real estate services Miami, property consulting, investment strategy, property management, financing solutions, market analysis');

    // Update or create canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', `${siteUrl}/services`);
    
    // Update last-modified
    let lastModified = document.querySelector('meta[name="last-modified"]');
    if (!lastModified) {
      lastModified = document.createElement('meta');
      lastModified.setAttribute('name', 'last-modified');
      document.head.appendChild(lastModified);
    }
    lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);

    // Add structured data
    const servicesSchema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Real Estate Consulting Services",
      "description": "Comprehensive real estate consulting services for global investors in Miami",
      "url": `${siteUrl}/services`,
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "item": {
            "@type": "Service",
            "name": "Strategic Real Estate Consulting",
            "description": "Define financial goals, create personalized investment strategies, and develop comprehensive real estate roadmaps",
            "provider": {
              "@type": "RealEstateAgent",
              "name": "Miami Comfort Group"
            }
          }
        },
        {
          "@type": "ListItem",
          "position": 2,
          "item": {
            "@type": "Service",
            "name": "Company & Legal Structure",
            "description": "LLC/Corp creation, coordination with legal and tax specialists, and asset structuring optimization",
            "provider": {
              "@type": "RealEstateAgent",
              "name": "Miami Comfort Group"
            }
          }
        },
        {
          "@type": "ListItem",
          "position": 3,
          "item": {
            "@type": "Service",
            "name": "Financing & Loans",
            "description": "Eligibility analysis, financing strategies with or without U.S. credit, and lender coordination",
            "provider": {
              "@type": "RealEstateAgent",
              "name": "Miami Comfort Group"
            }
          }
        },
        {
          "@type": "ListItem",
          "position": 4,
          "item": {
            "@type": "Service",
            "name": "Market Studies & Data Analysis",
            "description": "Comprehensive market studies, comparative analysis, and predictive profitability scenarios",
            "provider": {
              "@type": "RealEstateAgent",
              "name": "Miami Comfort Group"
            }
          }
        },
        {
          "@type": "ListItem",
          "position": 5,
          "item": {
            "@type": "Service",
            "name": "Property Search & Acquisition",
            "description": "Residential and investment property search, new construction opportunities, negotiation and closing",
            "provider": {
              "@type": "RealEstateAgent",
              "name": "Miami Comfort Group"
            }
          }
        },
        {
          "@type": "ListItem",
          "position": 6,
          "item": {
            "@type": "Service",
            "name": "Property Management",
            "description": "Provided by a licensed property management company within the MCG group — independent from real estate brokerage services.",
            "provider": {
              "@type": "Organization",
              "name": "MCG Property Management (independent from Avanti Way Realty)"
            }
          }
        },
        {
          "@type": "ListItem",
          "position": 7,
          "item": {
            "@type": "Service",
            "name": "Corporate Relocation",
            "description": "Relocation strategy for tech executives and professionals moving to Miami as companies establish new headquarters in South Florida",
            "provider": {
              "@type": "RealEstateAgent",
              "name": "Miami Comfort Group"
            }
          }
        }
      ]
    };
    
    let structuredDataScript = document.querySelector('script[data-schema="services"]');
    if (!structuredDataScript) {
      structuredDataScript = document.createElement('script');
      structuredDataScript.setAttribute('type', 'application/ld+json');
      structuredDataScript.setAttribute('data-schema', 'services');
      document.head.appendChild(structuredDataScript);
    }
    structuredDataScript.textContent = JSON.stringify(servicesSchema);
    
    // Cleanup function
    return () => {
      document.title = 'Miami Comfort Group';
    };
  }, []);

  const services = [
    {
      number: '01',
      icon: 'ri-line-chart-line',
      title: 'Strategic Real Estate Consulting',
      shortDesc: 'Comprehensive investment strategy and planning',
      fullDesc: 'We begin every client relationship with a deep dive into your financial goals, risk tolerance, and long-term vision. Our strategic consulting service provides you with a personalized investment roadmap that aligns with your objectives and market opportunities.',
      features: [
        'Financial goal definition and alignment',
        'Personalized investment strategy development',
        'Comprehensive risk analysis and mitigation planning',
        'Short, medium, and long-term real estate roadmap',
        'Market opportunity identification',
        'Portfolio diversification strategies'
      ],
      benefits: [
        'Clear direction and purpose for every investment',
        'Reduced risk through comprehensive planning',
        'Alignment with long-term financial objectives',
        'Data-driven decision-making framework'
      ]
    },
    {
      number: '02',
      icon: 'ri-building-line',
      title: 'Company & Legal Structure',
      shortDesc: 'Entity formation and asset structuring',
      fullDesc: 'Legal and tax services are provided by independent, Florida-licensed attorneys and CPAs.\n\nWe provide you with a curated selection of vetted professionals so you can choose the best fit for your needs.\n\nMiami Comfort Group does not provide legal or tax advice.\n\nProper legal structuring is essential for asset protection, tax optimization, and operational efficiency. We coordinate with top-tier legal and tax specialists to establish the optimal entity structure for your real estate investments.',
      features: [
        'LLC and Corporation formation',
        'Multi-entity structuring for portfolio management',
        'Coordination with specialized attorneys and CPAs',
        'Asset protection strategies',
        'Tax optimization planning',
        'Ongoing compliance management'
      ],
      benefits: [
        'Maximum asset protection and liability limitation',
        'Optimized tax treatment and deductions',
        'Professional entity management',
        'Peace of mind with expert legal coordination'
      ]
    },
    {
      number: '03',
      icon: 'ri-bank-line',
      title: 'Financing & Loans',
      shortDesc: 'Strategic financing solutions and lender coordination',
      fullDesc: 'Securing financing as a foreign investor or without established U.S. credit can be challenging. Our financing service provides eligibility analysis, strategic planning, and direct coordination with specialized lenders.\n\nFinancing services are coordinated with independent, NMLS-licensed mortgage professionals.\n\nWe provide you with a curated selection of vetted professionals so you can choose the best fit for your needs.\n\nMiami Comfort Group does not originate loans or provide mortgage advice.',
      features: [
        'Financing eligibility analysis',
        'Strategies for investors without U.S. credit history',
        'Foreign national loan programs',
        'Lender network and coordination',
        'Cash flow optimization strategies',
        'Refinancing and portfolio leverage planning'
      ],
      benefits: [
        'Access to specialized lender networks',
        'Competitive rates and terms',
        'Streamlined approval process',
        'Maximized leverage and cash flow'
      ]
    },
    {
      number: '04',
      icon: 'ri-bar-chart-box-line',
      title: 'Market Studies & Data Analysis',
      shortDesc: 'AI-powered market intelligence and predictive analytics',
      fullDesc: 'Our market analysis service combines human expertise with advanced AI technology to provide comprehensive market studies, comparative analysis, and predictive profitability scenarios.',
      features: [
        'Zone-specific market studies and trends',
        'Comparative market analysis (CMA)',
        'Historical profitability and appreciation data',
        'Predictive modeling and scenarios',
        'Neighborhood and demographic analysis',
        'Investment opportunity scoring'
      ],
      benefits: [
        'Data-driven investment decisions',
        'Reduced risk through comprehensive analysis',
        'Competitive advantage with predictive insights',
        'Clear understanding of market dynamics'
      ]
    },
    {
      number: '05',
      icon: 'ri-home-smile-line',
      title: 'Property Search & Acquisition',
      shortDesc: 'Curated property selection and expert negotiation',
      fullDesc: 'We leverage our extensive network and market knowledge to identify properties that align with your investment criteria. Our acquisition service includes property search, due diligence, negotiation, and closing coordination.',
      features: [
        'Residential and investment property search',
        'New construction opportunities',
        'Off-market property access',
        'Comprehensive due diligence',
        'Expert negotiation and offer strategy',
        'Closing coordination and support'
      ],
      benefits: [
        'Access to exclusive opportunities',
        'Professional negotiation for best terms',
        'Thorough due diligence and risk assessment',
        'Seamless closing process'
      ]
    },
    {
      number: '06',
      icon: 'ri-settings-3-line',
      title: 'Property Management',
      shortDesc: 'Provided by a licensed property management company within the MCG group',
      fullDesc: 'Provided by an independent property management company within the MCG group.\n\nWe provide you with a curated selection of vetted professionals so you can choose the best fit for your needs.\n\nThis service is not provided by Avanti Way Realty, LLC.',
      features: [
        'Full-service property management',
        'Short-term, mid-term, and long-term rental strategies',
        'Tenant screening and placement',
        'Maintenance and repair coordination',
        'Income and expense tracking',
        'Automated financial reporting dashboards'
      ],
      benefits: [
        'Hands-off investment management',
        'Maximized rental income',
        'Real-time financial visibility',
        'Professional tenant relations'
      ]
    },
    {
      number: '07',
      icon: 'ri-briefcase-line',
      title: 'Corporate Relocation',
      shortDesc: 'Executive relocation strategy for professionals moving to Miami',
      fullDesc: 'As major tech companies and financial firms establish new headquarters in South Florida, we help executives and professionals navigate the transition. From neighborhood selection to school districts, legal considerations, and closing on the right property — we provide end-to-end relocation support with the speed and precision the market demands.',
      features: [
        'Personalized neighborhood and lifestyle matching',
        'School district and family-oriented area guidance',
        'Coordination with corporate HR and relocation departments',
        'Expedited property search for tight timelines',
        'Temporary housing and lease negotiation',
        'Local orientation and community integration support'
      ],
      benefits: [
        'Seamless transition with minimal disruption',
        'Expert local knowledge for faster decisions',
        'End-to-end support from arrival to closing',
        'Strategic positioning before the market moves'
      ]
    }
  ];

  return (
    <>
      <div className="min-h-screen bg-white font-['Quicksand']">
        {/* Navigation */}
        <Navbar variant="solid" />

        {/* Hero Section */}
        <section className="pt-40 pb-32 bg-gradient-to-b from-[#E8E2DB] to-white">
          <div className="max-w-[1400px] mx-auto px-20">
            <div
              className="flex items-center gap-3 mb-8 transition-all duration-700 ease-out"
              style={{
                opacity: heroLoaded ? 1 : 0,
                transform: heroLoaded ? 'translateY(0)' : 'translateY(18px)',
                transitionDelay: '0ms',
              }}
            >
              <div className="w-3 h-3 rounded-full bg-[#A7D0CC]"></div>
              <span className="text-black text-[14px] uppercase tracking-[2px] font-medium">Our Services</span>
            </div>
            
            <h1
              className="font-['Oxygen'] font-bold text-[72px] leading-[1.1] text-black mb-12 max-w-[900px] transition-all duration-700 ease-out"
              style={{
                opacity: heroLoaded ? 1 : 0,
                transform: heroLoaded ? 'translateY(0)' : 'translateY(18px)',
                transitionDelay: '150ms',
              }}
            >
              Comprehensive Real Estate Consulting Solutions
            </h1>
            
            <p
              className="text-[#333333] text-[24px] leading-[1.6] max-w-[800px] transition-all duration-700 ease-out"
              style={{
                opacity: heroLoaded ? 1 : 0,
                transform: heroLoaded ? 'translateY(0)' : 'translateY(18px)',
                transitionDelay: '300ms',
              }}
            >
              Seven strategic pillars designed to guide you through every stage of your U.S. real estate investment journey—from initial strategy to ongoing management.
            </p>
          </div>
        </section>

        {/* Services Detail Section */}
        <section className="py-32 bg-white">
          <div className="max-w-[1400px] mx-auto px-20">
            {services.map((service, index) => (
              <SlideUpCard key={index} delay={0} className={`grid grid-cols-2 gap-20 items-center mb-32 ${index % 2 === 1 ? 'flex-row-reverse' : ''}`}>
                <div className={index % 2 === 1 ? 'order-2' : ''}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-full bg-[#A7D0CC] flex items-center justify-center">
                      <i className={`${service.icon} text-[32px] text-black`}></i>
                    </div>
                    <span className="font-['Oxygen'] font-bold text-[24px] text-[#A7D0CC]">{service.number}</span>
                  </div>
                  
                  <h2 className="font-['Oxygen'] font-bold text-[48px] leading-[1.2] text-black mb-6">
                    {service.title}
                  </h2>
                  
                  <p className="text-[#666666] text-[18px] leading-[1.8] mb-8">
                    {service.fullDesc.split('\n\n').map((para, i) => (
                      <span key={i} className="block mb-4 last:mb-0">{para}</span>
                    ))}
                  </p>

                  {/* Compliance disclosure for Financing & Loans */}
                  {service.number === '03' && (
                    <div className="border border-[#A7D0CC]/40 bg-[#A7D0CC]/8 rounded-xl px-7 py-5 mb-8" style={{backgroundColor: 'rgba(167,208,204,0.07)'}}>
                      <div className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-information-line text-[18px] text-[#A7D0CC]"></i>
                        </div>
                        <div>
                          <p className="font-['Oxygen'] font-bold text-[13px] text-black uppercase tracking-wider mb-3">
                            Financing & Loans — Service Disclosure
                          </p>
                          <p className="text-[#444444] text-[14px] leading-[1.7] mb-2">
                            Financing services are coordinated with independent, NMLS-licensed mortgage professionals.
                          </p>
                          <p className="text-[#444444] text-[14px] leading-[1.7] mb-2">
                            We provide you with a curated selection of vetted professionals so you can choose the best fit for your needs.
                          </p>
                          <p className="text-[#555555] text-[13px] leading-[1.6] italic">
                            Miami Comfort Group does not originate loans or provide mortgage advice.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Compliance disclosure for Company & Legal Structure */}
                  {service.number === '02' && (
                    <div className="border border-[#A7D0CC]/40 rounded-xl px-7 py-5 mb-8" style={{backgroundColor: 'rgba(167,208,204,0.07)'}}>
                      <div className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-information-line text-[18px] text-[#A7D0CC]"></i>
                        </div>
                        <div>
                          <p className="font-['Oxygen'] font-bold text-[13px] text-black uppercase tracking-wider mb-3">
                            Company & Legal Structure — Service Disclosure
                          </p>
                          <p className="text-[#444444] text-[14px] leading-[1.7] mb-2">
                            Legal and tax services are provided by independent, Florida-licensed attorneys and CPAs.
                          </p>
                          <p className="text-[#444444] text-[14px] leading-[1.7] mb-2">
                            We provide you with a curated selection of vetted professionals so you can choose the best fit for your needs.
                          </p>
                          <p className="text-[#555555] text-[13px] leading-[1.6] italic">
                            Miami Comfort Group does not provide legal or tax advice.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Compliance disclosure for Property Management */}
                  {service.number === '06' && (
                    <div className="border border-[#A7D0CC]/40 bg-[#A7D0CC]/8 rounded-xl px-7 py-5 mb-8" style={{backgroundColor: 'rgba(167,208,204,0.07)'}}>
                      <div className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-information-line text-[18px] text-[#A7D0CC]"></i>
                        </div>
                        <div>
                          <p className="font-['Oxygen'] font-bold text-[13px] text-black uppercase tracking-wider mb-3">
                            Property Management — Service Disclosure
                          </p>
                          <p className="text-[#444444] text-[14px] leading-[1.7] mb-2">
                            Provided by an independent property management company within the MCG group.
                          </p>
                          <p className="text-[#444444] text-[14px] leading-[1.7] mb-2">
                            We provide you with a curated selection of vetted professionals so you can choose the best fit for your needs.
                          </p>
                          <p className="text-[#555555] text-[13px] leading-[1.6] italic">
                            This service is not provided by Avanti Way Realty, LLC.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="bg-[#E8E2DB] rounded-2xl p-8 mb-8">
                    <h3 className="font-['Oxygen'] font-bold text-[20px] text-black mb-4">Key Features</h3>
                    <div className="space-y-3">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#A7D0CC] mt-2 flex-shrink-0"></div>
                          <span className="text-[#333333] text-[16px] leading-[1.6]">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-['Oxygen'] font-bold text-[20px] text-black mb-4">Benefits</h3>
                    <div className="space-y-3">
                      {service.benefits.map((benefit, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <i className="ri-checkbox-circle-fill text-[20px] text-[#A7D0CC] flex-shrink-0"></i>
                          <span className="text-[#333333] text-[16px] leading-[1.6]">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className={index % 2 === 1 ? 'order-1' : ''}>
                  <div className="relative rounded-3xl overflow-hidden aspect-[4/5]">
                    <img 
                      src={
                        index === 0 ? 'https://public.readdy.ai/ai/img_res/a6abdd170218d9aa918bea95e101faa0.jpg' :
                        index === 1 ? 'https://readdy.ai/api/search-image?query=Elegant%20law%20office%20conference%20room%20with%20legal%20documents%20and%20corporate%20structure%20diagrams%20on%20polished%20wooden%20table%2C%20professional%20attorneys%20reviewing%20LLC%20formation%20paperwork%2C%20modern%20Miami%20legal%20firm%20interior%20with%20leather%20chairs%20and%20contemporary%20art%2C%20sophisticated%20business%20atmosphere%20with%20warm%20ambient%20lighting%20and%20teal%20accent%20colors&width=800&height=1000&seq=legal-structure-mcg-02&orientation=portrait' :
                        index === 2 ? 'https://readdy.ai/api/search-image?query=Luxury%20bank%20interior%20with%20financial%20advisor%20presenting%20mortgage%20and%20loan%20options%20to%20international%20clients%2C%20modern%20banking%20hall%20with%20marble%20columns%20and%20digital%20rate%20displays%2C%20professional%20financing%20consultation%20scene%20in%20upscale%20Miami%20financial%20district%2C%20contemporary%20architecture%20with%20teal%20accent%20lighting%20and%20sophisticated%20ambiance&width=800&height=1000&seq=financing-loans-mcg-03&orientation=portrait' :
                        index === 3 ? 'https://readdy.ai/api/search-image?query=Advanced%20data%20analytics%20dashboard%20displaying%20real%20estate%20market%20trends%20and%20property%20investment%20metrics%20on%20large%20curved%20monitor%20screens%2C%20modern%20tech-enabled%20office%20with%20AI%20visualization%20tools%20and%20predictive%20modeling%20graphs%2C%20futuristic%20Miami%20workspace%20with%20holographic%20displays%20showing%20neighborhood%20demographics%20and%20appreciation%20forecasts%2C%20sleek%20contemporary%20design%20with%20teal%20accent%20colors&width=800&height=1000&seq=market-analysis-mcg-04&orientation=portrait' :
                        index === 4 ? 'https://readdy.ai/api/search-image?query=Stunning%20luxury%20Miami%20waterfront%20property%20exterior%20with%20modern%20architectural%20design%20and%20palm%20trees%2C%20elegant%20residential%20real%20estate%20showcase%20featuring%20contemporary%20glass%20facade%20and%20infinity%20pool%2C%20high-end%20investment%20property%20photography%20with%20golden%20hour%20lighting%20and%20sophisticated%20landscaping%2C%20premium%20real%20estate%20listing%20with%20teal%20accent%20elements%20in%20tropical%20setting&width=800&height=1000&seq=property-acquisition-mcg-05&orientation=portrait' :
                        index === 5 ? 'https://readdy.ai/api/search-image?query=Professional%20property%20manager%20conducting%20inspection%20of%20beautifully%20maintained%20luxury%20apartment%20interior%20with%20modern%20furnishings%20and%20smart%20home%20technology%2C%20elegant%20rental%20property%20showcase%20with%20pristine%20condition%20and%20designer%20decor%2C%20Miami%20high-rise%20residential%20unit%20with%20floor%20to%20ceiling%20windows%20and%20contemporary%20styling%2C%20premium%20property%20management%20service%20with%20teal%20accent%20colors%20and%20sophisticated%20atmosphere&width=800&height=1000&seq=property-management-mcg-06&orientation=portrait' :
                        'https://readdy.ai/api/search-image?query=Modern%20corporate%20executive%20relocating%20to%20Miami%20with%20luxury%20downtown%20skyline%20view%20from%20penthouse%20apartment%2C%20professional%20businessman%20reviewing%20neighborhood%20maps%20and%20property%20listings%20on%20tablet%2C%20sophisticated%20relocation%20consultation%20scene%20with%20moving%20boxes%20and%20elegant%20Miami%20condo%20interior%2C%20warm%20golden%20hour%20lighting%20with%20teal%20accent%20colors%20and%20contemporary%20tropical%20design%20elements&width=800&height=1000&seq=corporate-relocation-mcg-07&orientation=portrait'
                      }
                      alt={service.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </SlideUpCard>
            ))}
          </div>
        </section>

        {/* Process Section */}
        <section className="py-32 bg-[#E8E2DB]">
          <div className="max-w-[1400px] mx-auto px-20">
            <div className="text-center mb-20">
              <h2 className="font-['Oxygen'] font-bold text-[56px] text-black mb-6">
                Our Process
              </h2>
              <p className="text-[#666666] text-[20px] max-w-[700px] mx-auto">
                A structured approach to real estate investment success
              </p>
            </div>
            
            <div className="grid grid-cols-4 gap-8">
              {[
                {
                  step: '01',
                  title: 'Discovery',
                  description: 'Initial consultation to understand your goals, timeline, and investment criteria'
                },
                {
                  step: '02',
                  title: 'Strategy',
                  description: 'Develop comprehensive investment strategy with market analysis and financial planning'
                },
                {
                  step: '03',
                  title: 'Execution',
                  description: 'Implement strategy through entity formation, financing, and property acquisition'
                },
                {
                  step: '04',
                  title: 'Management',
                  description: 'Ongoing support with property management, reporting, and portfolio optimization'
                }
              ].map((step, index) => (
                <SlideUpCard key={index} delay={index * 100} className="relative">
                  <div className="bg-white rounded-2xl p-8 h-full">
                    <div className="text-[#A7D0CC] font-['Oxygen'] font-bold text-[48px] mb-4">{step.step}</div>
                    <h3 className="font-['Oxygen'] font-bold text-[24px] text-black mb-4">{step.title}</h3>
                    <p className="text-[#666666] text-[16px] leading-[1.7]">{step.description}</p>
                  </div>
                  {index < 3 && (
                    <div className="absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                      <i className="ri-arrow-right-line text-[32px] text-[#A7D0CC]"></i>
                    </div>
                  )}
                </SlideUpCard>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-32 bg-black">
          <div className="max-w-[900px] mx-auto px-20 text-center">
            <h2 className="font-['Oxygen'] font-bold text-[56px] text-white mb-8">
              Ready to Get Started?
            </h2>
            <p className="text-white/85 text-[20px] leading-[1.6] mb-12">
              Schedule a private consultation to discuss which services align with your investment goals and how we can support your real estate journey.
            </p>
            <a 
              href="/#contact" 
              className="inline-flex items-center justify-center w-full sm:w-auto px-7 sm:px-8 py-4 bg-[#A7D0CC] rounded-lg hover:bg-[#8CBEB9] transition-all duration-300 cursor-pointer shadow-md hover:shadow-[0_4px_12px_rgba(167,208,204,0.3)] min-h-[48px] whitespace-nowrap"
            >
              <span className="text-black text-[15px] sm:text-[16px] font-bold font-['Oxygen']">Schedule Consultation</span>
            </a>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-black relative overflow-hidden">
          <div className="absolute bottom-0 left-0 right-0 font-['Oxygen'] font-bold text-[180px] text-white/[0.03] text-center leading-none pointer-events-none select-none" style={{transform: 'translateY(40px)'}}>
            MCG
          </div>
          
          <div className="relative max-w-[1400px] mx-auto px-20 py-20">
            <div className="grid grid-cols-4 gap-16">
              <div>
                <img 
                  src="https://static.readdy.ai/image/3c69b41949a07696583ba6f972aa86db/7cc47221362597664001b9fe6feac620.png" 
                  alt="Miami Comfort Group" 
                  className="h-12 w-auto object-contain mb-12 brightness-0 invert"
                />
                <h3 className="font-['Oxygen'] font-bold text-[32px] text-white leading-[1.2] mb-8">
                  Strategic Real Estate<br />Consulting
                </h3>
                <p className="text-white/70 text-[15px] leading-[1.6]">
                  Investing well starts with deciding well
                </p>
              </div>
              
              <div>
                <h4 className="font-['Oxygen'] font-bold text-[16px] text-white uppercase tracking-wider mb-6">Services</h4>
                <div className="space-y-4">
                  {[
                    'Strategic Consulting',
                    'Company & Legal Structure',
                    'Financing & Loans',
                    'Market Studies',
                    'Property Search',
                    'Property Management'
                  ].map((link, index) => (
                    <div key={index}>
                      <a href="#" className="text-white/80 text-[15px] hover:text-[#A7D0CC] hover:opacity-100 transition-colors block">
                        {link}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="font-['Oxygen'] font-bold text-[16px] text-white uppercase tracking-wider mb-6">Company</h4>
                <div className="space-y-4">
                  <Link to="/about" className="text-white/80 text-[15px] hover:text-[#A7D0CC] hover:opacity-100 transition-colors block">
                    About Us
                  </Link>
                  <Link to="/services" className="text-white/80 text-[15px] hover:text-[#A7D0CC] hover:opacity-100 transition-colors block">
                    Services
                  </Link>
                  <Link to="/relocation" className="text-white/80 text-[15px] hover:text-[#A7D0CC] hover:opacity-100 transition-colors block">
                    Relocation
                  </Link>
                  <a href="/#technology" className="text-white/80 text-[15px] hover:text-[#A7D0CC] hover:opacity-100 transition-colors cursor-pointer block">
                    Technology
                  </a>
                  <a href="/#why-mcg" className="text-white/80 text-[15px] hover:text-[#A7D0CC] hover:opacity-100 transition-colors cursor-pointer block">
                    Why MCG
                  </a>
                  <a href="/#contact" className="text-white/80 text-[15px] hover:text-[#A7D0CC] hover:opacity-100 transition-colors cursor-pointer block">
                    Contact
                  </a>
                </div>
              </div>
              
              <div>
                <h4 className="font-['Oxygen'] font-bold text-[16px] text-white uppercase tracking-wider mb-6">Contact</h4>
                <div className="space-y-4 text-white/80 text-[15px]">
                  <p>Elisabet Sierra Bermejo</p>
                  <p>+1 786 856 8145</p>
                  <p>Miami, FL, USA</p>
                </div>
                
                <div className="flex items-center gap-5 mt-10">
                  <a href="https://www.instagram.com/elisabet_comfyhouses/" target="_blank" rel="noopener noreferrer" className="w-6 h-6 flex items-center justify-center cursor-pointer">
                    <i className="ri-instagram-line text-[24px] text-white/60 hover:text-[#A7D0CC] transition-colors"></i>
                  </a>
                  <a href="https://www.linkedin.com/in/elisabet-sierra/" target="_blank" rel="noopener noreferrer" className="w-6 h-6 flex items-center justify-center cursor-pointer">
                    <i className="ri-linkedin-line text-[24px] text-white/60 hover:text-[#A7D0CC] transition-colors"></i>
                  </a>
                  <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="w-6 h-6 flex items-center justify-center cursor-pointer">
                    <i className="ri-youtube-line text-[24px] text-white/60 hover:text-[#A7D0CC] transition-colors"></i>
                  </a>
                </div>
              </div>
            </div>
            
            <div className="mt-20 pt-8 border-t border-white/10 flex items-center justify-between">
              <p className="text-white/50 text-[14px]">
                © 2026 Miami Comfort Group. All rights reserved.
              </p>
              <a 
                href="https://readdy.ai/?ref=logo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white/50 text-[14px] hover:text-[#A7D0CC] transition-colors cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>

            {/* Legal Footer Text */}
            <div className="mt-8 pt-6 border-t border-white/10 text-center space-y-3">
              <p className="text-[11px] font-semibold text-[#cccccc]">
                Elisabet Sierra Bermejo · Licensed Real Estate Sales Associate<br />
                Avanti Way Realty, LLC · License #SL3535387
              </p>
              <p className="text-[10px] text-[#888888] max-w-[800px] mx-auto leading-relaxed">
                Miami Comfort Group LLC is a brand name used for marketing purposes only. 
                Real estate services are provided by Elisabet Sierra Bermejo, licensed 
                with Avanti Way Realty, LLC.
              </p>
              <p className="text-[10px] text-[#888888]">
                © 2026 Miami Comfort Group · miamicomfort.group · hello@miamicomfort.group
              </p>
              <p className="text-[9px] text-[#888888] max-w-[700px] mx-auto leading-relaxed pt-2">
                Equal Housing Opportunity. We are pledged to the letter and spirit of U.S. policy for equal housing opportunity throughout the nation.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}